import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import type { CfRealtimeEvent } from "@/modules/realtime/domain/contracts/realtime-event";
import type { FeedCommentRow } from "./types";

type FeedCommentsPage = {
  comments: FeedCommentRow[];
  total: number;
  limit: number;
  offset: number;
};

const REALTIME_COMMENT_PAGE_LIMIT = 20;
const FEED_REALTIME_FALLBACK_POLL_MS = 3_000;

/** SSE เป็นเส้นทางหลัก; poll เฉพาะช่วงที่การเชื่อมต่อ realtime หลุด */
export function feedDetailRealtimeRefetchInterval(
  connected: boolean,
  enabled: boolean,
): number | false {
  return enabled && !connected ? FEED_REALTIME_FALLBACK_POLL_MS : false;
}

/** Event สำคัญของหน้า feed detail — ไม่ broad-invalidate */
export const FEED_DETAIL_PRIORITY_EVENT_TYPES = [
  "comment.changed",
  "order.changed",
  "stock.changed",
] as const;

export type FeedDetailPriorityEventType =
  (typeof FEED_DETAIL_PRIORITY_EVENT_TYPES)[number];

export type RealtimeQueryFilter = {
  queryKey: readonly unknown[];
  exact?: boolean;
};

export type WorkspaceRealtimeScope = {
  id: string;
  feedId?: string | null;
  pageId?: string;
};

/** ใช้ขอบเขตงานจาก snapshot ที่ได้รับแล้ว ไม่อาศัย URL ของหน้า live/feed */
export function workspaceRealtimeQueryFilters(
  event: CfRealtimeEvent,
  scopes: WorkspaceRealtimeScope[],
): RealtimeQueryFilter[] {
  if (event.type === "display.changed" && event.entityId === "global-widget")
    return [
      { queryKey: ["global-widget-settings"] },
      { queryKey: ["workspace"] },
    ];
  if (event.type === "display.changed")
    return event.entityId
      ? [
          { queryKey: ["workspace", event.entityId] },
          { queryKey: ["workspace-catalog", event.entityId] },
          { queryKey: ["workspace-display-token", event.entityId] },
        ]
      : [];
  if (
    ![
      "order.changed",
      "stock.changed",
      "session.changed",
      "feed.changed",
      "live.changed",
    ].includes(event.type)
  )
    return [];
  const ids = new Set(
    scopes
      .filter((scope) =>
        event.feedId
          ? event.feedId === scope.feedId
          : Boolean(event.pageId && event.pageId === scope.pageId),
      )
      .map((scope) => scope.id),
  );
  return [...ids].flatMap((id) => [
    { queryKey: ["workspace", id] },
    ...(event.type === "order.changed"
      ? [{ queryKey: ["workspace-orders", id] }]
      : []),
    ...(event.type === "stock.changed" || event.type === "session.changed"
      ? [{ queryKey: ["workspace-catalog", id] }]
      : []),
  ]);
}

export function isFeedDetailPriorityEventType(
  type: string,
): type is FeedDetailPriorityEventType {
  return (FEED_DETAIL_PRIORITY_EVENT_TYPES as readonly string[]).includes(type);
}

/** อ่าน feed id จาก path detail หรือ monitor-display (ไม่รวม post-products) */
export function getFeedDetailIdFromPath(pathname: string): string | null {
  const displayMatch = pathname.match(
    /^\/app\/feeds\/([^/]+)\/monitor-display\/?$/,
  );
  if (displayMatch?.[1]) return displayMatch[1];

  const match = pathname.match(/^\/app\/feeds\/([^/]+)\/?$/);
  if (!match?.[1] || match[1] === "post-products") return null;
  return match[1];
}

/**
 * เลือก feedId สำหรับ targeted invalidate
 * - ใช้ event.feedId เป็นหลัก
 * - ถ้าไม่มี และอยู่หน้า detail → fallback URL (รองรับ order/stock ก่อน migration resolve)
 * - ถ้า event.feedId ต่างจากหน้า detail ที่เปิดอยู่ → null (ข้าม)
 */
export function resolveFeedDetailRealtimeTarget(input: {
  event: Pick<CfRealtimeEvent, "type" | "feedId">;
  pathname: string;
}): string | null {
  if (!isFeedDetailPriorityEventType(input.event.type)) return null;

  const activeFeedId = getFeedDetailIdFromPath(input.pathname);
  const eventFeedId = input.event.feedId?.trim() || null;

  if (eventFeedId && activeFeedId && eventFeedId !== activeFeedId) {
    return null;
  }

  return eventFeedId ?? activeFeedId;
}

/** query key prefixes ที่ต้อง refetch ตามชนิด event (mirror feedsKeys ใน queries.ts) */
export function feedDetailRealtimeQueryFilters(
  feedId: string,
  type: FeedDetailPriorityEventType,
): RealtimeQueryFilter[] {
  const detail = ["feeds", "detail", feedId] as const;
  switch (type) {
    case "comment.changed":
      return [{ queryKey: detail, exact: true }];
    case "order.changed":
      return [
        { queryKey: detail, exact: true },
        { queryKey: ["feeds", "detailCustomers", feedId] },
        { queryKey: [...detail, "productSales"], exact: true },
        { queryKey: [...detail, "paidProductSales"], exact: true },
        { queryKey: [...detail, "productMonitor"] },
      ];
    case "stock.changed":
      return [
        { queryKey: detail, exact: true },
        { queryKey: [...detail, "sessionProducts"] },
        { queryKey: [...detail, "productSales"], exact: true },
        { queryKey: [...detail, "paidProductSales"], exact: true },
        { queryKey: [...detail, "productMonitor"] },
      ];
  }
}

/** route realtime เฉพาะ query ที่ event นั้นกระทบจริง */
export function cfRealtimeQueryFilters(
  event: CfRealtimeEvent,
  pathname: string,
  scopes: WorkspaceRealtimeScope[] = [],
): RealtimeQueryFilter[] {
  const workspace = workspaceRealtimeQueryFilters(event, scopes);
  if (isFeedDetailPriorityEventType(event.type)) {
    const feedId = resolveFeedDetailRealtimeTarget({ event, pathname });
    return [
      ...workspace,
      ...(feedId ? feedDetailRealtimeQueryFilters(feedId, event.type) : []),
    ];
  }

  switch (event.type) {
    case "display.changed":
      return [
        ...workspace,
        ...(event.feedId
          ? [
              { queryKey: ["feeds", "detail", event.feedId, "productMonitor"] },
              {
                queryKey: ["feeds", "detail", event.feedId, "sessionProducts"],
              },
            ]
          : []),
      ];
    case "feed.changed":
      return [
        ...workspace,
        { queryKey: ["feeds", "list"] },
        ...(event.feedId
          ? [
              {
                queryKey: ["feeds", "detail", event.feedId],
                exact: true,
              } satisfies RealtimeQueryFilter,
            ]
          : []),
      ];
    case "session.changed":
      return [
        ...workspace,
        ...(event.feedId
          ? [
              {
                queryKey: ["feeds", "postContext", event.feedId],
                exact: true,
              },
              {
                queryKey: ["feeds", "detail", event.feedId],
                exact: true,
              },
              { queryKey: ["feeds", "detail", event.feedId, "productMonitor"] },
              {
                queryKey: ["feeds", "detail", event.feedId, "sessionProducts"],
              },
            ]
          : []),
      ];
    case "live.changed":
      return [...workspace, { queryKey: ["live"] }];
    case "facebook.changed":
      return [{ queryKey: ["facebook"] }];
    default:
      return [];
  }
}

/**
 * รวมหน้าแรกล่าสุดเข้ากับ comment ที่ผู้ใช้โหลดไว้แล้ว
 * โดยไม่ทิ้งหลายพันแถวที่กำลังอ่าน และปรับลำดับเก่าตาม total ใหม่
 */
export function mergeRealtimeCommentPage(
  current: InfiniteData<FeedCommentsPage, number> | undefined,
  latest: FeedCommentsPage,
): InfiniteData<FeedCommentsPage, number> {
  if (!current?.pages.length) {
    return { pages: [latest], pageParams: [0] };
  }

  const latestIds = new Set(latest.comments.map((comment) => comment.id));
  const seen = new Set(latestIds);
  const preserved: FeedCommentRow[] = [];

  for (const comment of current.pages.flatMap((page) => page.comments)) {
    if (seen.has(comment.id)) continue;
    seen.add(comment.id);
    preserved.push(comment);
  }

  const comments = [...latest.comments, ...preserved];
  return {
    pages: [
      {
        ...latest,
        comments,
        limit: comments.length,
        offset: 0,
      },
    ],
    pageParams: [0],
  };
}

/** ดึงเฉพาะหน้าแรกแล้ว merge เพื่อให้ realtime ไม่ reset/กระพริบทั้ง infinite list */
export async function refreshFeedCommentsRealtime(
  queryClient: QueryClient,
  feedId: string,
): Promise<void> {
  const { feedsApi } = await import("./client");
  const prefix = ["feeds", "detailComments", feedId] as const;
  const activeQueries = queryClient
    .getQueryCache()
    .findAll({ queryKey: prefix, type: "active" });

  await Promise.all(
    activeQueries.map(async (query) => {
      const q =
        typeof query.queryKey[3] === "string" ? query.queryKey[3] : undefined;
      const latest = await feedsApi.detailComments(feedId, {
        q,
        limit: REALTIME_COMMENT_PAGE_LIMIT,
        offset: 0,
      });
      queryClient.setQueryData<InfiniteData<FeedCommentsPage, number>>(
        query.queryKey,
        (current) => mergeRealtimeCommentPage(current, latest),
      );
    }),
  );
}

export function invalidateFeedDetailRealtimeQueries(
  queryClient: QueryClient,
  feedId: string,
  type: FeedDetailPriorityEventType,
): void {
  for (const filter of feedDetailRealtimeQueryFilters(feedId, type)) {
    void queryClient.invalidateQueries(
      {
        ...filter,
        refetchType: "active",
      },
      { cancelRefetch: false },
    );
  }
}
